import { useState } from 'react';

import {
  useClassifications,
  useCreateProject,
  useProject,
  useProjects,
  useTasks,
} from '../../api/projects.js';
import { useAdminTeams } from '../../api/admin.js';
import { useMe } from '../../api/hooks.js';
import { Layout } from '../../components/Layout.js';
import { ToastRegion } from '../../components/ToastRegion.js';
import { useToasts } from '../../components/useToasts.js';
import { usePageTitle } from '../../hooks/usePageTitle.js';
import { NewProjectDialog } from './dialogs/NewProjectDialog.js';
import { ProjectDetail } from './ProjectDetail.js';
import { ProjectTree } from './ProjectTree.js';
import './projects.css';

export function ProjectsPage() {
  usePageTitle('Projects & tasks');

  const meQuery = useMe();
  const projectsQuery = useProjects();
  const classificationsQuery = useClassifications();
  const createProjectMutation = useCreateProject();
  const me = meQuery.data;
  const isAdmin = me?.role === 'administrator';
  const adminTeamsQuery = useAdminTeams({ enabled: isAdmin });

  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [showNewProject, setShowNewProject] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { toasts, pushToast: addToast, dismiss: dismissToast } = useToasts();

  const selectedProjectQuery = useProject(selectedProjectId);
  const tasksQuery = useTasks(selectedProjectId);

  const teams = me?.teams ?? [];
  // Admins use the full admin teams list so they can assign to teams they aren't personally on.
  // Non-admins see only teams where they hold an approver role.
  const approverTeams = isAdmin
    ? (adminTeamsQuery.data ?? []).map((t) => ({
        teamId: t.teamId,
        name: t.teamName,
        role: 'approver' as const,
      }))
    : teams.filter((t) => t.role === 'approver');

  // Only approvers/admins can create projects; a non-approver lead can view and
  // manage projects they lead, but not spin up new ones (slice S-A).
  const canCreate = isAdmin || approverTeams.length > 0;

  function handleCreateProject(data: {
    name: string;
    code: string;
    description: string;
    teamId: string;
  }) {
    createProjectMutation.mutate(
      {
        name: data.name,
        code: data.code,
        description: data.description || undefined,
        teamId: data.teamId,
      },
      {
        onSuccess: (project) => {
          setShowNewProject(false);
          setSelectedProjectId(project.projectId);
          addToast('Project created', 'success');
        },
        onError: (err) => addToast(err.message, 'error'),
      },
    );
  }

  const loading =
    meQuery.isLoading ||
    projectsQuery.isLoading ||
    classificationsQuery.isLoading ||
    (isAdmin && adminTeamsQuery.isLoading);

  return (
    <Layout pageTitle="Projects & tasks">
      <div className="page-header">
        <h1>Projects &amp; tasks</h1>
        {!isAdmin && (
          <p style={{ margin: '4px 0 0', color: 'var(--fg3)', fontSize: 'var(--fs-14)' }}>
            You see only projects you lead or were granted access to.
          </p>
        )}
      </div>

      {loading && (
        <div aria-live="polite" style={{ color: 'var(--fg3)', padding: 'var(--space-4)' }}>
          Loading…
        </div>
      )}

      {!loading && (
        <div className="projects-layout">
          <ProjectTree
            projects={projectsQuery.data ?? []}
            tasks={tasksQuery.data ?? []}
            selectedProjectId={selectedProjectId}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            currentUserId={me?.userId ?? ''}
            isAdmin={isAdmin}
            canCreate={canCreate}
            onSelect={setSelectedProjectId}
            onNewProject={() => setShowNewProject(true)}
          />

          {selectedProjectId === null ? (
            <div className="projects-empty-detail">
              <span className="projects-empty-detail__icon" aria-hidden="true">
                📁
              </span>
              <p className="projects-empty-detail__text">
                {canCreate
                  ? 'Select a project from the sidebar, or create a new one.'
                  : 'Select a project from the sidebar.'}
              </p>
            </div>
          ) : selectedProjectQuery.isLoading ? (
            <div
              className="projects-empty-detail"
              aria-live="polite"
              style={{ color: 'var(--fg3)' }}
            >
              Loading project…
            </div>
          ) : selectedProjectQuery.data && classificationsQuery.data ? (
            <ProjectDetail
              project={selectedProjectQuery.data}
              classifications={classificationsQuery.data}
              teams={approverTeams}
              onToast={addToast}
            />
          ) : null}
        </div>
      )}

      <NewProjectDialog
        open={showNewProject}
        teams={approverTeams}
        onClose={() => setShowNewProject(false)}
        onSubmit={handleCreateProject}
        isPending={createProjectMutation.isPending}
      />

      <ToastRegion toasts={toasts} onDismiss={dismissToast} />
    </Layout>
  );
}
